import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, Users, Star, Check, Mail } from 'lucide-react';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { packages } from '../data/packages';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/PackageDetail.css';

const WHATSAPP_PHONE = '351968656613';
const BOOKING_EMAIL = 'bookings@madeiraoutdoors.com';

const PackageDetail = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const pkg = packages.find((p) => p.id === id);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  if (!pkg) return <Navigate to="/experiences" replace />;

  const whatsappMessage = t('packages.whatsappMsg', { title: pkg.title });
  const whatsappUrl = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(whatsappMessage)}`;

  const emailSubject = `Booking request — ${pkg.title}`;
  const emailBody = `Hi Madeira Outdoors team,\n\nI would like to book the "${pkg.title}" package (ref: ${pkg.id}).\n\nPlease send me more information about availability and next steps.\n\nThanks!`;
  const emailUrl = `mailto:${BOOKING_EMAIL}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

  return (
    <div className="package-detail-page" data-testid="package-detail-page">
      {/* Hero */}
      <section
        className="pkg-hero"
        style={{ backgroundImage: `linear-gradient(135deg, rgba(13,76,111,0.6) 0%, rgba(27,94,63,0.6) 100%), url(${pkg.image})` }}
      >
        <div className="container">
          <div className="pkg-hero-content">
            <Link to="/experiences" className="pkg-back" data-testid="pkg-back">
              <ArrowLeft size={18} /> {t('packages.backToPackages')}
            </Link>
            <span className="pkg-category">{pkg.category}</span>
            <h1 className="display-large pkg-h1">{pkg.title}</h1>
            <p className="body-large pkg-hero-sub">{pkg.shortDescription}</p>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="pkg-body-section">
        <div className="container">
          <div className="pkg-grid">
            {/* Left: Long description + highlights */}
            <div className="pkg-main">
              <div className="pkg-meta-bar">
                <div className="pkg-meta-item"><Clock size={20} /><span><strong>{t('packages.duration')}</strong><br />{pkg.duration}</span></div>
                <div className="pkg-meta-item"><Users size={20} /><span><strong>{t('packages.group')}</strong><br />{pkg.groupSize}</span></div>
                <div className="pkg-meta-item"><Star size={20} /><span><strong>{t('packages.difficulty')}</strong><br />{pkg.difficulty}</span></div>
              </div>

              <h2 className="heading-2 pkg-section-title">About this experience</h2>
              <div className="pkg-long-desc">
                {pkg.longDescription.split('\n\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>

              <h2 className="heading-2 pkg-section-title">{t('packages.highlights')}</h2>
              <ul className="pkg-highlights">
                {pkg.highlights.map((h, i) => (
                  <li key={i}><Check size={20} /> <span>{h}</span></li>
                ))}
              </ul>

              {pkg.includes && pkg.includes.length > 0 && (
                <>
                  <h2 className="heading-2 pkg-section-title">Included</h2>
                  <ul className="pkg-highlights">
                    {pkg.includes.map((h, i) => <li key={i}><Check size={20} /> <span>{h}</span></li>)}
                  </ul>
                </>
              )}

              {pkg.notIncludes && pkg.notIncludes.length > 0 && (
                <>
                  <h2 className="heading-2 pkg-section-title">Not included</h2>
                  <ul className="pkg-highlights pkg-highlights--no">
                    {pkg.notIncludes.map((h, i) => <li key={i}>— <span>{h}</span></li>)}
                  </ul>
                </>
              )}
            </div>

            {/* Right: Sticky Booking card */}
            <aside className="pkg-sidebar">
              <div className="pkg-booking-card">
                <div className="pkg-price-block">
                  <span className="pkg-price-label">{t('packages.from')}</span>
                  <span className="pkg-price">€{pkg.price}</span>
                  <span className="pkg-price-per">{t('packages.perPerson')}</span>
                </div>

                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary pkg-book-btn pkg-book-wa"
                  data-testid="pkg-book-whatsapp"
                >
                  <WhatsAppIcon size={20} /> {t('packages.bookByWhatsapp')}
                </a>

                <a
                  href={emailUrl}
                  className="btn-secondary pkg-book-btn"
                  data-testid="pkg-book-email"
                >
                  <Mail size={20} /> {t('packages.bookByEmail')}
                </a>

                <p className="pkg-booking-note">
                  No payment required up front — we tailor each booking by message.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PackageDetail;
